import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Film, Maximize2, Minimize2, Music, Newspaper, PictureInPicture2, Radio as RadioIcon, Pause, Play, Volume2, MicVocal, Lock, Sparkles, RefreshCw, Cloud, Coffee, Link2, Layers, Wand2, Upload, Mic as MicIcon, Columns2 } from "lucide-react";
import LiveBroadcaster from "./LiveBroadcaster";
import NewsroomOverlayRender from "./NewsroomOverlayRender";
import MusicPicker from "./MusicPicker";
import DirectorSoundboard from "./DirectorSoundboard";
import StudioMixerStrip from "./StudioMixerStrip";
import { WeatherOverlay, BrbOverlay, UrlPipOverlay, toEmbedUrl } from "./DirectorOverlays";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useFxCompositor } from "../lib/useFxCompositor";
import { buildFxChain, disposeChain } from "../lib/audioFxPresets";

// Announcer / voice-FX presets. Each preset returns a chain of WebAudio node
// factories, applied in order between the mic source and the destination.
const ANNOUNCER_PRESETS = {
  off: { label: "Off", chain: [] },
  podcast: {
    label: "Podcast",
    chain: [
      { type: "highpass", freq: 85 },
      { type: "compressor", threshold: -24, ratio: 3, attack: 0.003, release: 0.25 },
      { type: "peaking", freq: 5000, gain: 2, q: 1 }, // gentle presence lift
    ],
  },
  broadcast: {
    label: "Broadcast",
    chain: [
      { type: "highpass", freq: 90 },
      { type: "peaking", freq: 200, gain: -2, q: 0.8 }, // cut muddy low-mids
      { type: "peaking", freq: 4000, gain: 3, q: 1.2 }, // sibilance control region
      { type: "compressor", threshold: -20, ratio: 5, attack: 0.002, release: 0.15 },
      { type: "makeup", gain: 2 },
    ],
  },
  hype: {
    label: "Hype / DJ",
    chain: [
      { type: "peaking", freq: 120, gain: 4, q: 1 },    // big low-end
      { type: "peaking", freq: 3500, gain: 3, q: 1 },   // bite
      { type: "compressor", threshold: -16, ratio: 6, attack: 0.001, release: 0.1 },
      { type: "makeup", gain: 3 },
    ],
  },
};

// Six layout options for the director's split-screen stage. These are
// descriptive only — the actual slot rendering is done by <StageRenderer>.
const LAYOUTS = {
  solo:   { label: "Solo",        slots: 1, grid: "1 / 1" },
  pip:    { label: "PIP",         slots: 2, grid: "pip" },          // main + overlay tile (existing behaviour)
  side:   { label: "50 / 50",     slots: 2, grid: "grid-cols-2" },
  left60: { label: "60 L / 40 R", slots: 2, grid: "grid-cols-[60%_40%]" },
  right60:{ label: "40 L / 60 R", slots: 2, grid: "grid-cols-[40%_60%]" },
  topbot: { label: "Top / Bot.",  slots: 2, grid: "grid-rows-2" },
  triple: { label: "Triple",      slots: 3, grid: "grid-cols-3" },
  quad:   { label: "2 × 2 Quad",  slots: 4, grid: "grid-cols-2 grid-rows-2" },
};

// One-tap newsroom presets — drop a pre-built overlay in a single click.
export const NEWSROOM_PRESETS = {
  late_show: {
    label: "Late Show",
    lower_third: { visible: true, name: "Tonight's Guest", title: "In Conversation" },
    ticker: { visible: true, text: "Tonight on Tap IN TV · stick around for the monologue · VIP tips unlocked", speed: 20 },
    breaking: { visible: false, text: "" },
  },
  breaking_news: {
    label: "Breaking News",
    breaking: { visible: true, text: "BREAKING — developing story, details coming in" },
    lower_third: { visible: true, name: "LIVE", title: "Newsroom · Tap IN TV" },
    ticker: { visible: true, text: "Stay tuned · latest updates on the Tap IN TV Newsroom · share the stream", speed: 10 },
  },
  sermon: {
    label: "Sermon",
    lower_third: { visible: true, name: "Today's Message", title: "Tap IN Gospel" },
    ticker: { visible: true, text: "Prayer requests welcome in chat · tip the pastor via the heart icon", speed: 30 },
    breaking: { visible: false, text: "" },
  },
  sportscast: {
    label: "Sportscast",
    lower_third: { visible: true, name: "Home vs Away", title: "1st Quarter · 12:34" },
    ticker: { visible: true, text: "Scoreboard · stats update every minute · drop your predictions in chat", speed: 5 },
    breaking: { visible: false, text: "" },
  },
};

/**
 * AdvancedDirectorPanel — wraps LiveBroadcaster with director-grade tools.
 *
 * Gated features (admin / premium_creator / admin-granted regular creator):
 *  1. Multi-camera selector (up to 4 cameras enumerated from the OS)
 *  2. PIP (picture-in-picture) second camera tile overlaid on the host preview
 *  3. Music mixer (WebAudio mix of mic + music → replaceTrack on every PC)
 *  4. Newsroom overlays with one-tap presets
 *
 * Regular creators without the grant still see the plain LiveBroadcaster so
 * they can go live with camera + mic + chat.
 */
export default function AdvancedDirectorPanel({ streamId, kidsBrand, onEnd, embedded = false, onBroadcasterReady, onAudioReady, onStatusChange, fxSettings, cameraSettings, phoneCamStream = null }) {
  const { user } = useAuth();
  const canAdvanced = !!user && (user.role === "admin" || user.role === "premium_creator" || user.advanced_director_granted);

  const broadcasterRef = useRef(null);
  const [programDeviceId, setProgramDeviceId] = useState(null);

  // Live ref of audio panel state so the proxy's getState() is always fresh.
  const audioStateRef = useRef({ track: null, musicPlaying: false, musicVol: 0.5, announcer: "off" });

  // Publish broadcaster ref + panel helpers to the parent so the Director
  // Console can drive START/END + camera + music + announcer from outside.
  useEffect(() => {
    if (!onBroadcasterReady) return;
    const proxy = {
      get current() { return broadcasterRef.current; },
      // --- Camera control passthroughs so a sticky mobile preview bar (or
      // any external surface) can drive the camera without reaching into
      // the broadcaster ref directly. ---
      // When GS is active, return the GS-composited preview MediaStream
      // so the mobile sticky bar shows EXACTLY what viewers see (camera
      // + background keyed together), not the raw camera behind the
      // keyer.
      // Raw camera stream — the SOURCE for the mobile GS compositor.
      // MUST be the un-composited camera, or the compositor would read
      // its own output back in (feedback loop). iter212 fix: this used
      // to return getPreviewStream() (composited), while the studio's
      // GS bar ALSO fed from it — one of the two causes of the "chips
      // change but the preview never does" bug.
      getMediaStream: () => (broadcasterRef.current?.getMediaStream?.() || broadcasterRef.current?.streamRef?.current || null),
      // Composited preview stream (GS track + camera audio) — what the
      // host preview <video> and viewers actually see. iter212 fix:
      // this passthrough was MISSING entirely, so MobileLiveStudio's
      // `getPreviewStream?.() || raw` silently fell back to the raw
      // camera forever — THE root cause of the recurring "background
      // never changes" bug.
      getPreviewStream: () => (
        broadcasterRef.current?.getPreviewStream?.()
        || broadcasterRef.current?.getMediaStream?.()
        || broadcasterRef.current?.streamRef?.current
        || null
      ),
      getCompositedTrack: () => broadcasterRef.current?.getCompositedTrack?.() || null,
      // iter212 fix: MISSING passthrough — MobileGreenScreenBar's
      // composite track never reached LiveBroadcaster.setGsTrack, so
      // neither the WebRTC senders nor the preview ever swapped to it.
      setCompositedTrack: (t) => broadcasterRef.current?.setCompositedTrack?.(t ?? null),
      // Async stats passthrough for the Stream Health HUD.
      getOutboundStats: async () => (broadcasterRef.current?.getOutboundStats ? broadcasterRef.current.getOutboundStats() : null),
      flipCamera: () => broadcasterRef.current?.flipCamera?.(),
      toggleMic: () => broadcasterRef.current?.toggleMic?.(),
      getMicOn: () => broadcasterRef.current?.getMicOn?.() ?? true,
      reacquireCamera: (opts) => broadcasterRef.current?.reacquireCamera?.(opts),
      startBroadcast: () => broadcasterRef.current?.startBroadcast?.(),
      endBroadcast: () => broadcasterRef.current?.endBroadcast?.(),
      getStatus: () => broadcasterRef.current?.status,
      setProgramCamera: (deviceId) => setProgramCamera(deviceId),
      setTrack: (t) => setTrack(t),
      toggleMusicPlay: () => setMusicPlaying((v) => !v),
      setMusicVol: (v) => setMusicVol(v),
      setMasterVol: (v) => setMasterVol(v),
      setSfxVol: (v) => setSfxVol(v),
      setMicVol: (v) => setMicVol(v),
      setMic2Vol: (v) => setMic2Vol(v),
      setGuestVol: (v) => setGuestVol(Math.max(0, Math.min(1.5, v ?? 1))),
      setGuestMicOn: (on) => setGuestMicOn(!!on),
      setAutoDuck: (on) => setAutoDuck(!!on),
      getGuestState: () => ({
        active: String(pipDeviceId || "").startsWith("__guest__:") && !!pipStream,
        vol: guestVol,
        on: guestMicOn,
      }),
      // Generic per-channel volume setter — used by DirectorMixerRack so a
      // single fader path drives mic / mic2 / music / sfx / master without
      // each call site needing to know which gain node to poke.
      setChannelVol: (key, v) => {
        const clamp = Math.max(0, Math.min(1.5, v ?? 1));
        if (key === "mic") setMicVol(clamp);
        else if (key === "mic2") setMic2Vol(clamp);
        else if (key === "music") setMusicVol(clamp);
        else if (key === "sfx") setSfxVol(clamp);
        else if (key === "master") setMasterVol(clamp);
        else if (key === "guest") setGuestVol(clamp);
      },
      // Apply a Web Audio FX preset to ANY mic/music channel at runtime.
      // Honours the chain-swap logic in `_swapChannelFx` so we never have
      // to rebuild the whole graph or renegotiate WebRTC.
      setChannelFx: (key, fxKey) => {
        setChannelFx((prev) => ({ ...prev, [key]: fxKey || "off" }));
        _swapChannelFx(key, fxKey || "off");
      },
      // Add / remove a 2nd USB mic (or any audio input) without restarting
      // the broadcast. The host picks a deviceId from getAudioInputs() and
      // we acquire its track, splice it into the mix graph, and surface
      // it as the "mic2" channel in the rack. Returns the acquired stream.
      addMic2: async (deviceId) => {
        if (!deviceId) return null;
        try {
          const s = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: deviceId },
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: false,
            },
          });
          // Tear down any previous mic2 stream first.
          if (mic2Stream) {
            try { mic2Stream.getTracks().forEach((t) => t.stop()); } catch {}
          }
          setMic2DeviceId(deviceId);
          setMic2Stream(s);
          ensureMixGraph();
          // Wait one tick so the gain nodes exist if mix graph was just built.
          setTimeout(() => _attachMic2(audioCtxRef.current, s), 0);
          return s;
        } catch (e) {
          toast.error("Couldn't open that mic — pick a different input.");
          return null;
        }
      },
      removeMic2: () => {
        try { mic2Stream?.getTracks().forEach((t) => t.stop()); } catch {}
        try { channelSourceRef.current.mic2?.disconnect(); } catch {}
        disposeChain(channelFxChainRef.current.mic2);
        delete channelSourceRef.current.mic2;
        delete channelFxChainRef.current.mic2;
        setMic2Stream(null);
        setMic2DeviceId(null);
      },
      getAudioInputs: () => audioIn,
      getMic2State: () => ({ deviceId: mic2DeviceId, active: !!mic2Stream }),
      playSfx: (url) => {
        // Fire-and-forget SFX playback — used by scheduled ads / soundboard cues.
        if (!sfxElRef.current) return;
        try {
          sfxElRef.current.src = url;
          sfxElRef.current.play();
          ensureMixGraph();
        } catch (e) { /* fail soft */ }
      },
      openMusicPicker: () => setMusicOpen(true),
      setAnnouncer: (k) => setAnnouncer(k),
      ensureMix: () => ensureMixGraph(),
      getState: () => audioStateRef.current,
      // --- Newsroom overlays (used by DirectorConsole right-column controls) ---
      getOverlays: () => overlaysRef.current,
      setOverlayField: (path, val) => {
        // path like "lower_third.visible" or "ticker.text"
        const [a, b] = path.split(".");
        setOverlays((o) => ({ ...o, [a]: { ...o[a], [b]: val } }));
      },
      applyNewsroomPreset: (key) => {
        const p = NEWSROOM_PRESETS[key];
        if (!p) return;
        setOverlays((o) => ({
          ...o,
          lower_third: { ...o.lower_third, ...(p.lower_third || {}) },
          breaking: { ...o.breaking, ...(p.breaking || {}) },
          ticker: { ...o.ticker, ...(p.ticker || {}) },
        }));
      },
      clearOverlays: () => setOverlays((o) => ({
        ...o,
        lower_third: { ...o.lower_third, visible: false },
        breaking: { ...o.breaking, visible: false },
        ticker: { ...o.ticker, visible: false },
      })),
    };
    onBroadcasterReady(proxy);
    // eslint-disable-next-line
  }, []);
  const [overlays, setOverlays] = useState({
    lower_third: { visible: false, name: "", title: "" },
    breaking: { visible: false, text: "" },
    ticker: { visible: false, text: "", speed: 30 },
    pip: { visible: false, label: "Second Cam" },
  });
  // Keep a ref mirror so proxy.getOverlays() is always fresh
  const overlaysRef = useRef(overlays);
  useEffect(() => { overlaysRef.current = overlays; }, [overlays]);

  // One-tap newsroom preset — merge the preset's overlay config into current state,
  // preserving unrelated keys (e.g. pip) and any existing fields not in the preset.
  const applyPreset = (key) => {
    const p = NEWSROOM_PRESETS[key];
    if (!p) return;
    setOverlays((o) => ({
      ...o,
      lower_third: { ...o.lower_third, ...(p.lower_third || {}) },
      breaking: { ...o.breaking, ...(p.breaking || {}) },
      ticker: { ...o.ticker, ...(p.ticker || {}) },
    }));
  };
  const [pipStream, setPipStream] = useState(null);
  const [pipDeviceId, setPipDeviceId] = useState(null);
  const pipVideoRef = useRef(null);
  const [musicOpen, setMusicOpen] = useState(false);
  const [track, setTrack] = useState(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicVol, setMusicVol] = useState(0.5);
  const [masterVol, setMasterVol] = useState(1.0);
  const [sfxVol, setSfxVol] = useState(0.8);
  const [micVol, setMicVol] = useState(1.0);
  const [mic2Vol, setMic2Vol] = useState(1.0);
  const [mixOn, setMixOn] = useState(false);
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [bcStatus, setBcStatus] = useState("idle");
  const [mixerTick, setMixerTick] = useState(0); // bumped when the mix graph is built so StudioMixerStrip re-inits VU
  // Second mic — host can plug in a USB mic and route it through its own
  // channel + FX chain. Any future "guest mic" channels reuse this exact
  // pattern: stream → MediaStreamSource → fxChain → channelGain → master.
  const [mic2DeviceId, setMic2DeviceId] = useState(null);
  const [mic2Stream, setMic2Stream] = useState(null);
  // Guest channel — auto-attached when a WebRTC guest's MediaStream
  // arrives in `pipStream` via the `tapin:guest-track` event. Lets the
  // host fade / mute / FX the guest's voice independently of the host's
  // own mic. Removed automatically when the guest disconnects.
  const [guestVol, setGuestVol] = useState(1.0);
  const [guestMicOn, setGuestMicOn] = useState(true);
  // Auto-duck the host mic when the guest is speaking — pro broadcasts
  // use this to prevent the host from talking over their co-host. When
  // the guest's RMS crosses the threshold for >120ms, micGain ramps down
  // by ~7dB; after 280ms of guest silence it ramps back up. The rAF
  // loop is the single owner of micGain.gain while a guest is attached.
  const [autoDuck, setAutoDuck] = useState(true);
  // Rolling highlight buffer — keeps the last ~30s of the outbound
  // composite (PIP / side-by-side / Take-Full / GS all included) so the
  // host can one-tap save a clip while the show keeps running. Active
  // only while broadcasting (bcStatus === "live").
  const highlightChunksRef = useRef([]); // [{t, blob}, ...] capped to ~30s
  const highlightRecorderRef = useRef(null);
  const [highlightSaving, setHighlightSaving] = useState(false);
  const HIGHLIGHT_WINDOW_SEC = 30;

  // Rolling highlight recorder — runs while broadcasting. Records the
  // host's outbound composite stream in 1-second chunks; we keep only
  // the last HIGHLIGHT_WINDOW_SEC chunks in a circular buffer. When the
  // host taps "Save highlight", we glue the buffer into a single Blob
  // and POST it to /api/uploads/video → /api/videos.
  useEffect(() => {
    if (bcStatus !== "live") {
      // Tear down any prior recorder when broadcast ends / pauses.
      try { highlightRecorderRef.current?.stop?.(); } catch {}
      highlightRecorderRef.current = null;
      highlightChunksRef.current = [];
      return undefined;
    }
    let cancelled = false;
    // Small delay so the broadcaster has finalised the outbound senders
    // before we sample them.
    const t = setTimeout(() => {
      if (cancelled) return;
      const rec = broadcasterRef.current?.getRecordableStream?.();
      if (!rec || !rec.getVideoTracks().length) return;
      let mime = "video/webm;codecs=vp9,opus";
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) {
        mime = "video/webm;codecs=vp8,opus";
      }
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) {
        mime = "video/webm";
      }
      try {
        const r = new MediaRecorder(rec, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
        r.ondataavailable = (e) => {
          if (!e.data || !e.data.size) return;
          const buf = highlightChunksRef.current;
          buf.push({ t: Date.now(), blob: e.data });
          // Evict anything older than the rolling window (+ a 3s grace
          // so partial chunks at the boundary aren't lost on save).
          const cutoff = Date.now() - (HIGHLIGHT_WINDOW_SEC + 3) * 1000;
          while (buf.length && buf[0].t < cutoff) buf.shift();
        };
        r.onerror = () => { /* swallow — the broadcast is the priority */ };
        r.start(1000);
        highlightRecorderRef.current = r;
      } catch (e) {
        console.warn("highlight recorder boot failed:", e);
      }
    }, 800);
    return () => {
      cancelled = true;
      clearTimeout(t);
      try { highlightRecorderRef.current?.stop?.(); } catch {}
      highlightRecorderRef.current = null;
      highlightChunksRef.current = [];
    };
  }, [bcStatus]);

  const saveHighlight = async () => {
    if (highlightSaving) return;
    const chunks = highlightChunksRef.current;
    if (!chunks.length) {
      toast.error("No highlight yet — wait a few seconds after going live.");
      return;
    }
    setHighlightSaving(true);
    try {
      const blob = new Blob(chunks.map((c) => c.blob), { type: chunks[0].blob.type || "video/webm" });
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([blob], `highlight-${ts}.webm`, { type: blob.type });
      const form = new FormData();
      form.append("file", file);
      // Step 1: upload binary to object storage.
      const up = await api.post("/uploads/video", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const storage_path = up?.data?.storage_path;
      if (!storage_path) throw new Error("Upload returned no storage_path");
      // Step 2: create the video row. The backend's thumbnail_gen
      // sweeper will auto-generate a Nano Banana cover from the title.
      const title = `Live Highlight — ${new Date().toLocaleString()}`;
      const res = await api.post("/videos", {
        title: title.slice(0, 180),
        description: `Captured from the live broadcast on ${new Date().toLocaleString()}.`,
        category: "Highlights",
        tags: ["highlight", "live"],
        video_url: null,
        storage_path,
        thumbnail_url: null,
        duration_sec: Math.min(HIGHLIGHT_WINDOW_SEC, Math.round((chunks[chunks.length - 1].t - chunks[0].t) / 1000) + 1),
        is_premium: false,
        members_only: false,
        is_ppv: false,
        format: "wide",
        rating: "G",
        scheduled_release: null,
        is_premiere: false,
        is_short: false,
      });
      const vid = res?.data;
      toast.success(
        vid?.id ? "Highlight saved! Generating thumbnail…" : "Highlight saved!",
        {
          action: vid?.id
            ? { label: "Open", onClick: () => window.open(`/watch/${vid.id}`, "_blank") }
            : undefined,
          duration: 5000,
        }
      );
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save highlight — try again.");
    } finally {
      setHighlightSaving(false);
    }
  };
  // Active FX preset per channel — { mic: 'podcast', mic2: 'echo', music: 'reverb', ... }.
  // Updated at runtime via broadcasterProxy.setChannelFx(); the corresponding
  // FX node-chain is rebuilt and spliced into the audio graph immediately.
  const [channelFx, setChannelFx] = useState({});
  const audioElRef = useRef(null);
  const sfxElRef = useRef(null);
  const audioCtxRef = useRef(null);
  const musicGainRef = useRef(null);
  const masterGainRef = useRef(null);
  const sfxGainRef = useRef(null);
  const micGainRef = useRef(null);
  const mic2GainRef = useRef(null);
  const guestGainRef = useRef(null);
  // Auto-duck plumbing — analyser sits between the guest FX chain and
  // the guest gain; the rAF loop reads RMS, applies a smoothed
  // multiplier to micGain so the host's voice drops when the guest is
  // active. Live-tracking refs (vs. closures) so updates land instantly.
  const guestAnalyserRef = useRef(null);
  const duckRafRef = useRef(0);
  const duckMultRef = useRef(1.0); // 1.0 normal, ~0.45 when ducking
  const micVolRef = useRef(1.0);
  const micOnRef = useRef(true);
  const autoDuckRef = useRef(true);
  const guestMicOnRef = useRef(true);
  // Source nodes per channel — kept so we can hot-swap FX chains without
  // rebuilding the whole mix graph (which would force renegotiation).
  const channelSourceRef = useRef({}); // { mic: node, mic2: node, music: node, sfx: node }
  const channelFxChainRef = useRef({}); // { mic: {head,tail,nodes}, mic2: {...} }
  const destRef = useRef(null);
  const announcerChainRef = useRef([]);

  // --- NEW director tools state (iter30) ---
  // Hydrate the initial layout from the same localStorage key the
  // DirectorConsole's right-rail buttons write to — so toggling Split
  // / PIP / Grid in Full Studio actually reaches the embedded
  // broadcaster panel, not just the right rail.
  const [layout, setLayout] = useState(() => {
    try {
      const v = localStorage.getItem("tapin_stage_layout");
      return ["solo", "split", "pip", "grid"].includes(v) ? v : "solo";
    } catch { return "solo"; }
  });
  // Cross-tab / cross-component sync: listen for `storage` events on
  // the same key so the embedded panel re-renders when the right-rail
  // button is clicked. We also support a synchronous CustomEvent
  // `tapin:stage-layout` so same-tab updates are reflected without
  // waiting for storage's debounced fire.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "tapin_stage_layout" || !e.newValue) return;
      if (["solo", "split", "pip", "grid"].includes(e.newValue)) setLayout(e.newValue);
    };
    const onCustom = (e) => {
      const v = e?.detail;
      if (["solo", "split", "pip", "grid"].includes(v)) setLayout(v);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("tapin:stage-layout", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("tapin:stage-layout", onCustom);
    };
  }, []);
  const [weather, setWeather] = useState({ visible: false, location: "" });
  const [brb, setBrb] = useState({ visible: false, text: "Be Right Back", image_url: "" });
  const [urlPip, setUrlPip] = useState({ visible: false, url: "", label: "Embed" });
  const [announcer, setAnnouncer] = useState("off");
  const [musicLibrary, setMusicLibrary] = useState([]);
  const [playlist, setPlaylist] = useState([]); // ordered list of track objects
  const [playlistIdx, setPlaylistIdx] = useState(0);
  const brbFileRef = useRef(null);
  const musicUploadRef = useRef(null);

  const media = broadcasterRef.current?.media; // shared media hook from broadcaster
  const devices = media?.devices?.videoIn || [];
  const audioIn = media?.devices?.audioIn || [];

  // Keep the proxy's getState() view in sync with the latest audio panel state.
  useEffect(() => {
    audioStateRef.current = { track, musicPlaying, musicVol, announcer };
  }, [track, musicPlaying, musicVol, announcer]);

  // When the host picks a new program camera, call media.selectDevice to reacquire
  const setProgramCamera = async (deviceId) => {
    if (!deviceId || !media) return;
    setProgramDeviceId(deviceId);
    await media.selectDevice({ videoDeviceId: deviceId });
  };

  // ---- Studio FX: runtime chromakey + virtual BG on the MAIN outbound camera ----
  // When `fxSettings.chroma.enabled` AND `fxSettings.scope.main` are both true, we
  // pipe the current camera track through a canvas compositor and swap the result
  // onto every live PeerConnection. When any of those flips off, we restore the
  // original camera track so the audience sees the raw feed again.
  //
  // iter-180 (2026-05-01): When the embedded LiveBroadcaster is handling
  // green-screen via its own `useGreenScreen` hook (admin / premium-creator
  // chip in the preview), this legacy StudioFxModal-driven compositor must
  // STAY OFF — otherwise BOTH compositors race on the outbound video
  // sender, each calling `replaceTrack` after the other, and the host's
  // selected background never wins. The embedded broadcaster exposes
  // `canUseGreenScreen` via its streamRef proxy so we can short-circuit
  // cleanly here.
  const embeddedHasGsChip =
    !!(broadcasterRef.current?.canUseGreenScreen ?? (!!user && ["admin", "premium_creator"].includes(user?.role || "")));
  const fxActive = !embeddedHasGsChip && !!(fxSettings?.chroma?.enabled && fxSettings?.scope?.main);
  const getSourceStream = useCallback(() => broadcasterRef.current?.streamRef?.current, []);
  const onCompositedTrack = useCallback((track) => {
    const proxy = broadcasterRef.current;
    if (!proxy) return;
    if (track) {
      proxy.replaceVideoTrack?.(track);
    } else {
      // Restore the original camera track
      const original = proxy.streamRef?.current?.getVideoTracks?.()[0];
      if (original) proxy.replaceVideoTrack?.(original);
    }
  }, []);
  useFxCompositor({
    active: fxActive,
    settings: fxSettings,
    getSourceStream,
    onCompositedTrack,
  });

  // Secondary camera (PIP) — either a physical secondary USB camera (via
  // getUserMedia) or the paired phone-cam MediaStream coming from above.
  const PHONE_CAM_DEVICE_ID = "__phone_cam__";
  const setPipCamera = async (deviceId) => {
    if (!deviceId) return;
    try {
      // Stop previous pip stream if it was a locally-acquired one (don't stop
      // the phone stream — it's owned by the parent component).
      if (pipStream && pipDeviceId !== PHONE_CAM_DEVICE_ID) {
        pipStream.getTracks().forEach((t) => t.stop());
      }
      if (deviceId === PHONE_CAM_DEVICE_ID) {
        if (!phoneCamStream) return;
        setPipStream(phoneCamStream);
        setPipDeviceId(deviceId);
        if (pipVideoRef.current) pipVideoRef.current.srcObject = phoneCamStream;
        setOverlays((o) => ({ ...o, pip: { ...o.pip, visible: true, label: o.pip.label || "Phone" } }));
        return;
      }
      const s = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } }, audio: false });
      setPipStream(s);
      setPipDeviceId(deviceId);
      if (pipVideoRef.current) pipVideoRef.current.srcObject = s;
      setOverlays((o) => ({ ...o, pip: { ...o.pip, visible: true } }));
    } catch (e) {
      console.warn("Could not acquire PIP camera:", e);
    }
  };

  // "Take phone cam full" — replace the main program video track with the
  // phone's video.  The original camera track is stashed on `_restoreTrack`
  // so we can flip back via takeMainFull().
  const [phoneOnAir, setPhoneOnAir] = useState(false);
  const takePhoneFull = () => {
    const proxy = broadcasterRef.current;
    const phoneTrack = phoneCamStream?.getVideoTracks?.()?.[0];
    if (!proxy || !phoneTrack) return;
    proxy.replaceVideoTrack?.(phoneTrack);
    setPhoneOnAir(true);
  };
  const takeMainFull = () => {
    const proxy = broadcasterRef.current;
    const main = proxy?.streamRef?.current?.getVideoTracks?.()[0];
    // Stop side-by-side compositor if it was running — its captureStream
    // track is about to be replaced with the raw camera track.
    stopSideBySide();
    if (main) proxy.replaceVideoTrack?.(main);
    setPhoneOnAir(false);
    setGuestOnAir(false);
  };

  // "Take guest full-screen" — swap the program video track to the guest's
  // incoming WebRTC video. Used during the "pass the mic" moment when the
  // guest goes from PIP tile to headliner. takeMainFull() flips it back.
  const [guestOnAir, setGuestOnAir] = useState(false);
  const takeGuestFull = () => {
    const proxy = broadcasterRef.current;
    // The guest stream lives in pipStream (we routed it there in the
    // tapin:guest-track listener). Grab its first video track and shove
    // it onto every active viewer PC via the broadcaster proxy.
    const guestTrack = pipStream?.getVideoTracks?.()?.[0];
    if (!proxy || !guestTrack) return;
    // Stop side-by-side if it was running.
    stopSideBySide();
    proxy.replaceVideoTrack?.(guestTrack);
    setGuestOnAir(true);
    setPhoneOnAir(false);
  };

  // "Side-by-Side" host + guest at 50/50 on the program track. Composites
  // both video streams onto an off-screen canvas and pushes the canvas's
  // captureStream() track to every viewer PC. The host's `<video>`
  // preview reflects the composite automatically because
  // `replaceVideoTrack` also resets the host's preview srcObject.
  const [sideBySideOn, setSideBySideOn] = useState(false);
  const sbsRafRef = useRef(0);
  const sbsCanvasRef = useRef(null);
  const sbsVidHostRef = useRef(null);
  const sbsVidGuestRef = useRef(null);
  const sbsCaptureTrackRef = useRef(null);
  const stopSideBySide = () => {
    if (sbsRafRef.current) cancelAnimationFrame(sbsRafRef.current);
    sbsRafRef.current = 0;
    try { sbsCaptureTrackRef.current?.stop?.(); } catch {}
    sbsCaptureTrackRef.current = null;
    try { sbsVidHostRef.current?.pause?.(); } catch {}
    try { sbsVidGuestRef.current?.pause?.(); } catch {}
    sbsVidHostRef.current = null;
    sbsVidGuestRef.current = null;
    sbsCanvasRef.current = null;
    setSideBySideOn(false);
  };
  const takeSideBySide = async () => {
    const proxy = broadcasterRef.current;
    const hostStream = proxy?.streamRef?.current;
    const guestStream = pipStream;
    if (!proxy || !hostStream || !guestStream) return;
    // Build two off-screen <video> elements so the canvas can draw each
    // stream's current frame — we can't ctx.drawImage a MediaStream
    // directly, only a HTMLVideoElement that's playing it.
    const vHost = document.createElement("video");
    vHost.muted = true; vHost.playsInline = true; vHost.autoplay = true;
    vHost.srcObject = hostStream;
    const vGuest = document.createElement("video");
    vGuest.muted = true; vGuest.playsInline = true; vGuest.autoplay = true;
    vGuest.srcObject = guestStream;
    // Wait for both to have at least one frame so the first composite
    // isn't black.
    try { await Promise.all([vHost.play(), vGuest.play()]); } catch { /* autoplay race — fine */ }
    sbsVidHostRef.current = vHost;
    sbsVidGuestRef.current = vGuest;
    // 1280x720 canvas — splits to two 640x720 panels.
    const canvas = document.createElement("canvas");
    canvas.width = 1280;
    canvas.height = 720;
    sbsCanvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    // Object-cover scaling so each side fills its half without bars.
    const drawCover = (vid, dx) => {
      const dw = 640, dh = 720;
      if (!vid.videoWidth || !vid.videoHeight) return;
      const srcAR = vid.videoWidth / vid.videoHeight;
      const dstAR = dw / dh;
      let sx = 0, sy = 0, sw = vid.videoWidth, sh = vid.videoHeight;
      if (srcAR > dstAR) {
        // source wider than dest — crop sides
        sw = vid.videoHeight * dstAR;
        sx = (vid.videoWidth - sw) / 2;
      } else {
        // source taller — crop top/bottom
        sh = vid.videoWidth / dstAR;
        sy = (vid.videoHeight - sh) / 2;
      }
      ctx.drawImage(vid, sx, sy, sw, sh, dx, 0, dw, dh);
    };
    const tick = () => {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, 1280, 720);
      drawCover(vHost, 0);
      drawCover(vGuest, 640);
      // Thin divider so the two panels don't blur into each other.
      ctx.fillStyle = "rgba(255,255,255,0.15)";
      ctx.fillRect(638, 0, 4, 720);
      sbsRafRef.current = requestAnimationFrame(tick);
    };
    tick();
    const cs = canvas.captureStream(30);
    const track = cs.getVideoTracks?.()[0];
    if (!track) return;
    sbsCaptureTrackRef.current = track;
    proxy.replaceVideoTrack?.(track);
    setSideBySideOn(true);
    setGuestOnAir(false);
    setPhoneOnAir(false);
  };

  // If the phone stream disappears (unpaired / browser tab closed), roll
  // back any phone-linked state so the UI doesn't lie.
  useEffect(() => {
    if (!phoneCamStream) {
      if (pipDeviceId === PHONE_CAM_DEVICE_ID) stopPip();
      if (phoneOnAir) takeMainFull();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneCamStream]);

  const stopPip = () => {
    // Only stop tracks if we OWN them — the phone stream is owned by the
    // parent component and must outlive this PIP selection.
    if (pipStream && pipDeviceId !== PHONE_CAM_DEVICE_ID) {
      pipStream.getTracks().forEach((t) => t.stop());
    }
    setPipStream(null); setPipDeviceId(null);
    setOverlays((o) => ({ ...o, pip: { ...o.pip, visible: false } }));
  };

  // Guest publish ingest — when the embedded LiveBroadcaster receives an
  // incoming WebRTC stream from a waiting-room guest it dispatches a
  // `tapin:guest-track` window event. Auto-route the first arriving
  // guest into the PIP slot so the host can see them on the program
  // stage with zero extra clicks. We only auto-take the slot when it's
  // currently empty so a host who's already mid-PIP-with-second-cam
  // doesn't get hijacked.
  useEffect(() => {
    const onGuestTrack = (e) => {
      const { guestId, stream } = e.detail || {};
      if (!stream) return;
      if (pipStream) return; // host has a local PIP — don't steal it
      setPipStream(stream);
      setPipDeviceId(`__guest__:${guestId}`);
      if (pipVideoRef.current) pipVideoRef.current.srcObject = stream;
      setOverlays((o) => ({ ...o, pip: { ...o.pip, visible: true, label: o.pip.label || "Guest" } }));
    };
    const onGuestEnd = (e) => {
      const { guestId } = e.detail || {};
      if (pipDeviceId === `__guest__:${guestId}`) {
        setPipStream(null); setPipDeviceId(null);
        setOverlays((o) => ({ ...o, pip: { ...o.pip, visible: false } }));
        // Guest disappeared while their cam was full-screen or in
        // side-by-side → fall back to the host's main camera so the
        // broadcast doesn't freeze on the last guest frame.
        if (guestOnAir || sideBySideOn) takeMainFull();
      }
    };
    window.addEventListener("tapin:guest-track", onGuestTrack);
    window.addEventListener("tapin:guest-track-end", onGuestEnd);
    return () => {
      window.removeEventListener("tapin:guest-track", onGuestTrack);
      window.removeEventListener("tapin:guest-track-end", onGuestEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipStream, pipDeviceId, guestOnAir, sideBySideOn]);

  // Broadcast overlay changes to viewers whenever overlays state mutates
  useEffect(() => {
    broadcasterRef.current?.sendOverlays?.(overlays);
  }, [overlays]);

  // Cleanup streams on unmount
  useEffect(() => () => {
    if (pipStream) pipStream.getTracks().forEach((t) => t.stop());
    try { audioCtxRef.current?.close(); } catch {}
  }, [pipStream]);

  // ----- MUSIC MIXER (Web Audio) — with optional announcer FX chain on the mic -----
  const buildAnnouncerChain = (ctx, presetKey) => {
    // Tear down previous chain
    announcerChainRef.current.forEach((n) => { try { n.disconnect(); } catch {} });
    const preset = ANNOUNCER_PRESETS[presetKey] || ANNOUNCER_PRESETS.off;
    const nodes = [];
    for (const step of preset.chain) {
      let node;
      if (step.type === "highpass") {
        node = ctx.createBiquadFilter();
        node.type = "highpass";
        node.frequency.value = step.freq;
      } else if (step.type === "peaking") {
        node = ctx.createBiquadFilter();
        node.type = "peaking";
        node.frequency.value = step.freq;
        node.gain.value = step.gain;
        node.Q.value = step.q;
      } else if (step.type === "compressor") {
        node = ctx.createDynamicsCompressor();
        node.threshold.value = step.threshold;
        node.ratio.value = step.ratio;
        node.attack.value = step.attack;
        node.release.value = step.release;
      } else if (step.type === "makeup") {
        node = ctx.createGain();
        node.gain.value = Math.pow(10, step.gain / 20);
      }
      if (node) nodes.push(node);
    }
    announcerChainRef.current = nodes;
    return nodes;
  };

  const ensureMixGraph = () => {
    if (audioCtxRef.current) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const dest = ctx.createMediaStreamDestination();
    destRef.current = dest;
    const micGain = ctx.createGain(); micGain.gain.value = micVol;
    const mic2Gain = ctx.createGain(); mic2Gain.gain.value = mic2Vol;
    const guestGain = ctx.createGain(); guestGain.gain.value = guestMicOn ? guestVol : 0;
    const musicGain = ctx.createGain(); musicGain.gain.value = musicVol;
    const sfxGain = ctx.createGain(); sfxGain.gain.value = sfxVol;
    const masterGain = ctx.createGain(); masterGain.gain.value = masterVol;
    micGainRef.current = micGain;
    mic2GainRef.current = mic2Gain;
    guestGainRef.current = guestGain;
    musicGainRef.current = musicGain;
    sfxGainRef.current = sfxGain;
    masterGainRef.current = masterGain;
    // Every sub-gain goes into the master bus, then master → dest so one fader
    // rules them all. Per-fader controls in the rack still work — they adjust
    // their own gain node (mic / mic2 / music / sfx) before the master bus.
    masterGain.connect(dest);
    // Mic → [announcer chain (legacy)] → [perChannel FX] → micGain → masterGain
    const micStream = broadcasterRef.current?.streamRef?.current;
    if (micStream && micStream.getAudioTracks().length) {
      const src = ctx.createMediaStreamSource(new MediaStream(micStream.getAudioTracks()));
      channelSourceRef.current.mic = src;
      const chain = buildAnnouncerChain(ctx, announcer);
      let tail = src;
      for (const n of chain) { tail.connect(n); tail = n; }
      // Splice the per-channel FX (initially "off" → passthrough) so future
      // setChannelFx('mic', 'echo') swaps without rebuilding the whole graph.
      const fx = buildFxChain(ctx, channelFx.mic || "off");
      channelFxChainRef.current.mic = fx;
      tail.connect(fx.head); fx.tail.connect(micGain); micGain.connect(masterGain);
    }
    // Mic 2 (optional second mic the host plugged in) — we wire its source
    // into the graph here when the stream has been acquired by the time we
    // initialise. If acquired later, _attachMic2() does the same splicing.
    if (mic2Stream && mic2Stream.getAudioTracks().length) {
      _attachMic2(ctx, mic2Stream);
    }
    // Guest channel — when an incoming WebRTC guest is already in the
    // PIP slot at graph-init time, splice their audio in. Otherwise the
    // `pipStream` useEffect below will call `_attachGuest` once they
    // arrive.
    if (String(pipDeviceId || "").startsWith("__guest__:") && pipStream?.getAudioTracks?.().length) {
      _attachGuest(ctx, pipStream);
    }
    // Music <audio> → [perChannel FX] → musicGain → masterGain (+ local monitor)
    if (audioElRef.current) {
      const musicSrc = ctx.createMediaElementSource(audioElRef.current);
      channelSourceRef.current.music = musicSrc;
      const fx = buildFxChain(ctx, channelFx.music || "off");
      channelFxChainRef.current.music = fx;
      musicSrc.connect(fx.head); fx.tail.connect(musicGain); musicGain.connect(masterGain);
      musicSrc.connect(ctx.destination);
    }
    // SFX <audio> → sfxGain → masterGain (used by future SFX pads / scheduled ad cues)
    if (sfxElRef.current) {
      try {
        const sfxSrc = ctx.createMediaElementSource(sfxElRef.current);
        channelSourceRef.current.sfx = sfxSrc;
        sfxSrc.connect(sfxGain).connect(masterGain);
      } catch {}
    }
    // Notify parent (Director Console) that real audio is ready so its right-
    // side MixerRack can attach its AnalyserNode to `dest.stream` for live VU.
    onAudioReady?.({ audioCtx: ctx, outputDest: dest });
    setMixerTick((t) => t + 1);
  };

  // Wire a Mic 2 MediaStream into the existing mix graph. Idempotent — if
  // mic2 was previously wired we tear it down first so swapping devices
  // doesn't double-feed the master bus.
  const _attachMic2 = (ctx, stream) => {
    if (!ctx || !stream || !stream.getAudioTracks?.().length) return;
    const masterGain = masterGainRef.current;
    const mic2Gain = mic2GainRef.current;
    if (!masterGain || !mic2Gain) return;
    // Tear down old mic2 chain
    try { channelSourceRef.current.mic2?.disconnect(); } catch {}
    disposeChain(channelFxChainRef.current.mic2);
    const src = ctx.createMediaStreamSource(new MediaStream(stream.getAudioTracks()));
    channelSourceRef.current.mic2 = src;
    const fx = buildFxChain(ctx, channelFx.mic2 || "off");
    channelFxChainRef.current.mic2 = fx;
    src.connect(fx.head); fx.tail.connect(mic2Gain); mic2Gain.connect(masterGain);
  };

  // Wire a guest's incoming WebRTC audio into the mix graph as the
  // "guest" channel — same pattern as Mic 2 but the source MediaStream
  // comes from the host's RTCPeerConnection (received via the
  // `tapin:guest-track` event). Idempotent so swapping the active guest
  // (one guest leaves, another joins the PIP) doesn't double-feed master.
  const _attachGuest = (ctx, stream) => {
    if (!ctx || !stream || !stream.getAudioTracks?.().length) return;
    const masterGain = masterGainRef.current;
    const guestGain = guestGainRef.current;
    if (!masterGain || !guestGain) return;
    try { channelSourceRef.current.guest?.disconnect(); } catch {}
    disposeChain(channelFxChainRef.current.guest);
    const src = ctx.createMediaStreamSource(new MediaStream(stream.getAudioTracks()));
    channelSourceRef.current.guest = src;
    const fx = buildFxChain(ctx, channelFx.guest || "off");
    channelFxChainRef.current.guest = fx;
    src.connect(fx.head); fx.tail.connect(guestGain); guestGain.connect(masterGain);
    // Tap an analyser right after the FX (pre-gain) so the duck logic
    // reads the guest's actual voice level regardless of where the
    // host has parked the guest fader.
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.5;
    try { fx.tail.connect(analyser); } catch {}
    guestAnalyserRef.current = analyser;
    _startDuckLoop(ctx);
  };

  const _detachGuest = () => {
    try { channelSourceRef.current.guest?.disconnect(); } catch {}
    disposeChain(channelFxChainRef.current.guest);
    delete channelSourceRef.current.guest;
    delete channelFxChainRef.current.guest;
    _stopDuckLoop();
  };

  // Sidechain auto-duck — the rAF reads guest RMS and smoothly scales
  // micGain via setTargetAtTime so the host's voice drops while the
  // guest is talking. Single owner of micGain.gain while running.
  const _startDuckLoop = (ctx) => {
    if (duckRafRef.current) return;
    const buf = new Uint8Array(1024);
    const THRESHOLD = 0.04;   // RMS floor — above this counts as "speaking"
    const ATTACK_MS = 120;    // duck after this many ms of speech
    const RELEASE_MS = 280;   // release after this many ms of silence
    const DUCK_AMOUNT = 0.45; // -7dB-ish
    let speakingSince = 0;
    let silentSince = performance.now();
    const tick = () => {
      const analyser = guestAnalyserRef.current;
      const mg = micGainRef.current;
      if (!analyser || !mg) { duckRafRef.current = requestAnimationFrame(tick); return; }
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const now = performance.now();
      if (rms > THRESHOLD) {
        if (!speakingSince) speakingSince = now;
        silentSince = 0;
      } else {
        if (!silentSince) silentSince = now;
        speakingSince = 0;
      }
      const guestAudible = autoDuckRef.current && guestMicOnRef.current;
      const ducking = guestAudible && speakingSince && (now - speakingSince) > ATTACK_MS;
      const releasing = !guestAudible || (silentSince && (now - silentSince) > RELEASE_MS);
      if (ducking) duckMultRef.current = DUCK_AMOUNT;
      else if (releasing) duckMultRef.current = 1.0;
      // Smooth ramp — setTargetAtTime gives a natural exponential approach.
      const target = (micOnRef.current ? micVolRef.current : 0) * duckMultRef.current;
      try { mg.gain.setTargetAtTime(target, ctx.currentTime, 0.08); } catch {}
      duckRafRef.current = requestAnimationFrame(tick);
    };
    duckRafRef.current = requestAnimationFrame(tick);
  };

  const _stopDuckLoop = () => {
    if (duckRafRef.current) cancelAnimationFrame(duckRafRef.current);
    duckRafRef.current = 0;
    duckMultRef.current = 1.0;
    guestAnalyserRef.current = null;
    // Restore raw mic gain — the useEffect on micVol/micOn now owns it
    // again since duckRafRef is cleared.
    if (micGainRef.current) {
      const ctx = audioCtxRef.current;
      const target = micOnRef.current ? micVolRef.current : 0;
      try { micGainRef.current.gain.setTargetAtTime(target, ctx ? ctx.currentTime : 0, 0.05); } catch {
        micGainRef.current.gain.value = target;
      }
    }
  };

  // Re-wire FX chain for any channel without rebuilding the entire graph.
  // Disposes the old chain, builds the new one, splices it between source
  // and channel gain. Must be called from the proxy's setChannelFx().
  const _swapChannelFx = (channelKey, fxKey) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const src = channelSourceRef.current[channelKey];
    if (!src) return;
    const gainMap = {
      mic: micGainRef.current, mic2: mic2GainRef.current,
      music: musicGainRef.current, sfx: sfxGainRef.current,
      guest: guestGainRef.current,
    };
    const gain = gainMap[channelKey];
    if (!gain) return;
    // Disconnect source from whatever it currently points at and dispose
    // the old FX chain. Then build a fresh chain and re-connect.
    try { src.disconnect(); } catch {}
    disposeChain(channelFxChainRef.current[channelKey]);
    const fx = buildFxChain(ctx, fxKey || "off");
    channelFxChainRef.current[channelKey] = fx;
    src.connect(fx.head); fx.tail.connect(gain);
  };

  const toggleMix = async () => {
    if (!mixOn) {
      // Start mix
      if (!track) return;
      ensureMixGraph();
      try { await audioCtxRef.current.resume(); } catch {}
      if (audioElRef.current) {
        try { await audioElRef.current.play(); setMusicPlaying(true); } catch {}
      }
      const mixedTrack = destRef.current?.stream?.getAudioTracks?.()[0];
      if (mixedTrack) broadcasterRef.current?.replaceAudioTrack?.(mixedTrack);
      setMixOn(true);
    } else {
      // Stop mix — revert to raw mic track
      try { audioElRef.current?.pause(); } catch {}
      setMusicPlaying(false);
      const micTrack = broadcasterRef.current?.streamRef?.current?.getAudioTracks?.()[0];
      if (micTrack) broadcasterRef.current?.replaceAudioTrack?.(micTrack);
      setMixOn(false);
    }
  };

  useEffect(() => { if (musicGainRef.current) musicGainRef.current.gain.value = musicVol; }, [musicVol]);
  useEffect(() => {
    micVolRef.current = micVol;
    micOnRef.current = micOn;
    // When the duck loop is active it owns micGain.gain, so don't set
    // it directly here — the next rAF tick will pick up the new
    // micVolRef value via the `target = micVol * duck` formula. We only
    // poke micGain directly when no guest is attached (no duck loop).
    if (!duckRafRef.current && micGainRef.current) {
      micGainRef.current.gain.value = micOn ? micVol : 0;
    }
  }, [micVol, micOn]);
  useEffect(() => { if (mic2GainRef.current) mic2GainRef.current.gain.value = mic2Vol; }, [mic2Vol]);
  useEffect(() => {
    // Live-update the guest channel gain when the host moves the fader
    // or toggles the guest-mute button.
    if (guestGainRef.current) guestGainRef.current.gain.value = guestMicOn ? guestVol : 0;
    guestMicOnRef.current = guestMicOn;
  }, [guestVol, guestMicOn]);
  useEffect(() => { autoDuckRef.current = autoDuck; }, [autoDuck]);
  useEffect(() => { if (masterGainRef.current) masterGainRef.current.gain.value = masterVol; }, [masterVol]);
  useEffect(() => { if (sfxGainRef.current) sfxGainRef.current.gain.value = sfxVol; }, [sfxVol]);
  // When mic2Stream arrives AFTER the mix graph already exists, re-attach.
  useEffect(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || !mic2Stream) return;
    _attachMic2(ctx, mic2Stream);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mic2Stream]);

  // Auto-attach / detach the guest audio channel when the PIP slot is
  // (or stops being) sourced from a WebRTC guest. Pre-emptively spins
  // up the mix graph so the host can fade the guest the moment they
  // arrive — no extra "Pipe mix" click needed before the slider works.
  useEffect(() => {
    const isGuest = String(pipDeviceId || "").startsWith("__guest__:");
    if (isGuest && pipStream?.getAudioTracks?.().length) {
      ensureMixGraph();
      _attachGuest(audioCtxRef.current, pipStream);
    } else {
      _detachGuest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipStream, pipDeviceId]);

  const camTiles = useMemo(() => devices.slice(0, 4), [devices]);

  return (
    <div data-testid="advanced-director-panel" className="space-y-4">
      {/* Always-mounted hidden SFX player. Driven via broadcasterProxy.playSfx(url). */}
      <audio ref={sfxElRef} crossOrigin="anonymous" data-testid="director-sfx-audio" className="hidden" />
      {/* Studio mixer strip — hidden when embedded inside the Director Console (the console's toolbar + MixerRack own these controls) */}
      {!embedded && (
        <StudioMixerStrip
          status={bcStatus}
          onStart={() => broadcasterRef.current?.startBroadcast?.()}
          onEnd={() => { broadcasterRef.current?.endBroadcast?.(); onEnd?.(); }}
          audioCtx={audioCtxRef.current}
          outputDest={destRef.current}
          commentsEnabled={commentsEnabled}
          onToggleComments={(next) => {
            setCommentsEnabled(next);
            broadcasterRef.current?.sendCommentsToggle?.(next);
          }}
        />
      )}

      {/* Host preview with overlays rendered on top */}
      <div className="relative">
        <LiveBroadcaster ref={broadcasterRef} streamId={streamId} kidsBrand={kidsBrand} hideChat={true} hideCrewBoost={embedded} hideInlineControls={embedded} cameraSettings={cameraSettings} onEnd={onEnd} onStatusChange={(s) => { setBcStatus(s); onStatusChange?.(s); }} />
        {/* Render overlays on top of the broadcaster's preview area. Position over the whole panel — overlays clamp themselves. */}
        <div className="absolute inset-0 pointer-events-none">
          <NewsroomOverlayRender overlays={overlays} />
          {overlays.pip?.visible && (
            <div data-testid="pip-tile" className="absolute top-4 right-4 w-40 md:w-56 aspect-video rounded-lg overflow-hidden border-2 border-tv-accent shadow-2xl bg-black pointer-events-auto">
              <video ref={pipVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <div className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-[10px] uppercase tracking-wider font-bold text-center rounded px-1 py-0.5">{overlays.pip.label || "Cam 2"}</div>
              {/* One-tap "Take guest full" + "Side-by-Side" — only show
                  when the PIP source is a published guest (vs. local cam
                  2 / phone). Three states: Take Full, Side-by-Side, or
                  Back to PIP (restores main cam). */}
              {String(pipDeviceId || "").startsWith("__guest__:") && (
                <div className="absolute top-1 left-1 right-1 flex items-center gap-1 flex-wrap">
                  {(guestOnAir || sideBySideOn) ? (
                    <button
                      data-testid="director-guest-back-to-pip"
                      onClick={takeMainFull}
                      className="inline-flex items-center gap-1 bg-black/75 hover:bg-black/90 text-white text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 border border-white/15"
                      title="Bring host back as the program — guest returns to PIP"
                    >
                      <Minimize2 className="w-3 h-3" /> Back to PIP
                    </button>
                  ) : (
                    <>
                      <button
                        data-testid="director-guest-take-full"
                        onClick={takeGuestFull}
                        className="inline-flex items-center gap-1 bg-tv-live hover:bg-red-600 text-white text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 shadow-lg"
                        title="Promote the guest to the full program — host becomes the PIP"
                      >
                        <Maximize2 className="w-3 h-3" /> Take Full
                      </button>
                      <button
                        data-testid="director-guest-side-by-side"
                        onClick={takeSideBySide}
                        className="inline-flex items-center gap-1 bg-tv-accent hover:bg-yellow-400 text-black text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 shadow-lg"
                        title="50 / 50 split — host on the left, guest on the right"
                      >
                        <Columns2 className="w-3 h-3" /> 50 / 50
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <WeatherOverlay visible={weather.visible} location={weather.location} />
          <UrlPipOverlay visible={urlPip.visible} url={urlPip.url} label={urlPip.label} />
          <BrbOverlay visible={brb.visible} text={brb.text} kidsBrand={kidsBrand} customImageUrl={brb.image_url} />
          {layout !== "solo" && layout !== "pip" && (
            <div data-testid="stage-layout-badge" className="absolute top-4 left-4 bg-black/70 backdrop-blur rounded-full px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-white border border-white/10">
              Stage · {LAYOUTS[layout].label}
            </div>
          )}
          {/* Save Highlight floating action — only visible while live.
              Lets the host one-tap save the last 30s of the outbound
              composite as a publishable VOD clip without leaving the
              broadcast. */}
          {bcStatus === "live" && (
            <button
              type="button"
              onClick={saveHighlight}
              disabled={highlightSaving}
              data-testid="director-save-highlight"
              className={`pointer-events-auto absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-bold shadow-2xl border transition ${
                highlightSaving
                  ? "bg-white/10 text-white/60 border-white/10 cursor-wait"
                  : "bg-tv-live hover:bg-red-600 text-white border-red-400/50"
              }`}
              title="Save the last 30 seconds of this broadcast as a clip on your channel"
            >
              <Film className="w-3.5 h-3.5" />
              {highlightSaving ? "Saving…" : "Save Highlight · 30s"}
            </button>
          )}
        </div>
      </div>

      {!canAdvanced && (
        <div data-testid="advanced-director-gate" className="tv-glass rounded-xl border border-white/10 p-5 flex items-start gap-4">
          <Lock className="w-6 h-6 text-tv-accent shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-display font-bold text-white">Unlock Director's Advanced Kit</div>
            <p className="text-white/60 text-sm mt-1">Multi-camera, Picture-in-Picture, music mixer, and newsroom overlays are unlocked for Premium Creators — or ask a Tap IN TV admin to grant access to your account.</p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Link to="/subscribe" data-testid="advanced-upgrade-btn" className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-4 py-2 rounded-full">Upgrade to Premium Creator</Link>
              <Link to="/contact" data-testid="advanced-request-btn" className="bg-white/10 hover:bg-white/20 text-white font-bold text-xs px-4 py-2 rounded-full">Request admin grant</Link>
            </div>
          </div>
        </div>
      )}

      {!embedded && canAdvanced && (
      <div className="grid md:grid-cols-3 gap-4">
        {/* 1. Multi-cam */}
        <section data-testid="director-multicam" className="tv-glass rounded-xl border border-white/10 p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-white font-display font-bold text-sm"><Camera className="w-4 h-4 text-tv-accent" /> Cameras <span className="text-white/40 text-[10px] font-semibold">up to 4</span></div>
            <button
              type="button"
              onClick={() => media?.refreshDevices?.()}
              data-testid="director-refresh-cams"
              title="Refresh camera list"
              className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          {camTiles.length === 0 && <div className="text-white/50 text-xs">Plug in a camera then tap the refresh icon above.</div>}
          <div className="grid grid-cols-2 gap-2">
            {camTiles.map((d, i) => {
              const active = programDeviceId === d.deviceId || (!programDeviceId && media?.current?.videoDeviceId === d.deviceId);
              return (
                <button
                  key={d.deviceId || i}
                  data-testid={`director-cam-${i}`}
                  onClick={() => setProgramCamera(d.deviceId)}
                  className={`rounded-lg border text-left px-2.5 py-2 text-[11px] transition ${active ? "border-tv-live bg-tv-live/15 text-white" : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10"}`}
                >
                  <div className="font-bold truncate">{d.label || `Camera ${i + 1}`}</div>
                  <div className="text-[9px] uppercase tracking-wider opacity-70">{active ? "PROGRAM" : "Tap to switch"}</div>
                </button>
              );
            })}
          </div>
          {/* Quick cam + mic on/off (same control as the broadcaster toolbar — duplicated here so the director doesn't have to aim for the tiny pill). */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                const t = broadcasterRef.current?.streamRef?.current?.getVideoTracks?.()[0];
                if (!t) return;
                t.enabled = !t.enabled;
                setCamOn(t.enabled);
              }}
              aria-pressed={camOn}
              data-testid="director-cam-toggle"
              className={`rounded-lg px-2 py-2 text-[11px] font-bold flex items-center justify-center gap-1 transition ${camOn ? "bg-white/10 text-white hover:bg-white/15" : "bg-red-500/30 text-red-200 border border-red-500/50"}`}
            >
              {camOn ? "📹 Camera On" : "🚫 Camera Off"}
            </button>
            <button
              type="button"
              onClick={() => {
                const t = broadcasterRef.current?.streamRef?.current?.getAudioTracks?.()[0];
                if (!t) return;
                t.enabled = !t.enabled;
                setMicOn(t.enabled);
              }}
              aria-pressed={micOn}
              data-testid="director-mic-toggle"
              className={`rounded-lg px-2 py-2 text-[11px] font-bold flex items-center justify-center gap-1 transition ${micOn ? "bg-white/10 text-white hover:bg-white/15" : "bg-red-500/30 text-red-200 border border-red-500/50"}`}
            >
              {micOn ? "🎤 Mic On" : "🔇 Mic Off"}
            </button>
          </div>
          <div className="border-t border-white/10 pt-3 mt-3">
            <div className="flex items-center gap-2 text-white text-xs font-semibold mb-2"><PictureInPicture2 className="w-3.5 h-3.5 text-tv-accent" /> Picture-in-picture</div>
            <select
              value={pipDeviceId || ""}
              onChange={(e) => e.target.value ? setPipCamera(e.target.value) : stopPip()}
              data-testid="director-pip-select"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent"
            >
              <option value="">PIP off</option>
              {phoneCamStream && (
                <option value={PHONE_CAM_DEVICE_ID} data-testid="director-pip-phone-option">📱 Phone Camera</option>
              )}
              {camTiles.map((d, i) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
              ))}
            </select>
            {phoneCamStream && (
              <div className="mt-2 flex items-center gap-1.5" data-testid="director-phone-take-controls">
                {!phoneOnAir ? (
                  <button
                    onClick={takePhoneFull}
                    data-testid="director-take-phone-full"
                    className="flex-1 bg-sky-500 hover:bg-sky-400 text-black font-bold text-[10px] px-2 py-1.5 rounded inline-flex items-center justify-center gap-1"
                  >📱 Take phone FULL</button>
                ) : (
                  <button
                    onClick={takeMainFull}
                    data-testid="director-take-main-full"
                    className="flex-1 bg-amber-400 hover:bg-amber-300 text-black font-bold text-[10px] px-2 py-1.5 rounded inline-flex items-center justify-center gap-1 animate-pulse"
                  >← Back to main camera</button>
                )}
                <span className="text-[9px] text-white/50 italic">Phone is live · use PIP above for side-by-side</span>
              </div>
            )}
            <input
              type="text"
              value={overlays.pip.label}
              onChange={(e) => setOverlays({ ...overlays, pip: { ...overlays.pip, label: e.target.value } })}
              placeholder="PIP label (e.g. Guest, Crowd)"
              data-testid="director-pip-label"
              className="mt-2 w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent"
            />
            {/* Guest mic fader — only renders when the PIP is sourced from
                a WebRTC guest. Owns its own gain node in the mix graph,
                so it works the moment the guest joins. Going "live" with
                guest audio still requires the host to click "Pipe mix to
                stream" — without it, only the host mic reaches viewers. */}
            {String(pipDeviceId || "").startsWith("__guest__:") && (
              <div data-testid="director-guest-audio-strip" className="mt-3 pt-3 border-t border-white/10 space-y-2">
                <div className="flex items-center gap-2 text-white text-[11px] font-bold">
                  <MicIcon className="w-3.5 h-3.5 text-tv-accent" /> Guest mic
                  {!mixOn && (
                    <span className="ml-auto text-[9px] text-amber-300/80 font-semibold italic">
                      Pipe mix to broadcast
                    </span>
                  )}
                </div>
                <label className="block">
                  <div className="flex items-center justify-between text-[10px] text-white/60 uppercase tracking-wider font-bold">
                    <span className="flex items-center gap-1"><Volume2 className="w-3 h-3" /> Volume</span>
                    <span>{Math.round(guestVol * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    data-testid="director-guest-vol"
                    min="0" max="1.5" step="0.05"
                    value={guestVol}
                    onChange={(e) => setGuestVol(parseFloat(e.target.value))}
                    className="w-full accent-tv-accent"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setGuestMicOn((v) => !v)}
                  aria-pressed={!guestMicOn}
                  data-testid="director-guest-mute"
                  className={`w-full font-bold text-[11px] px-2 py-1.5 rounded-full inline-flex items-center justify-center gap-1.5 ${guestMicOn ? "bg-white/10 hover:bg-white/15 text-white" : "bg-red-500/30 text-red-200 border border-red-500/50"}`}
                  title={guestMicOn ? "Mute the guest's microphone in the broadcast mix" : "Unmute the guest's microphone"}
                >
                  {guestMicOn ? <><MicIcon className="w-3 h-3" /> Mute guest</> : <><MicIcon className="w-3 h-3" /> Unmute guest</>}
                </button>
                <button
                  type="button"
                  onClick={() => setAutoDuck((v) => !v)}
                  aria-pressed={autoDuck}
                  data-testid="director-guest-autoduck"
                  className={`w-full font-bold text-[11px] px-2 py-1.5 rounded-full inline-flex items-center justify-center gap-1.5 ${autoDuck ? "bg-tv-accent/20 text-tv-accent border border-tv-accent/40" : "bg-white/5 hover:bg-white/10 text-white/60 border border-white/10"}`}
                  title="When ON, the host mic auto-dips ~7 dB while the guest is speaking, then returns smoothly when the guest pauses."
                >
                  <Sparkles className="w-3 h-3" /> {autoDuck ? "Auto-duck: ON" : "Auto-duck: off"}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* 2. Music mixer */}
        <section data-testid="director-music-mixer" className="tv-glass rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-white font-display font-bold text-sm mb-3"><Music className="w-4 h-4 text-tv-accent" /> Music & Mixer</div>
          {!track ? (
            <button
              onClick={() => setMusicOpen(true)}
              data-testid="director-music-pick-btn"
              className="w-full bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-3 py-2 rounded-full"
            >
              Pick a track
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {track.album_art && <img src={track.album_art} alt="" className="w-10 h-10 rounded object-cover" />}
                <div className="min-w-0 flex-1">
                  <div className="text-white text-xs font-semibold truncate">{track.title}</div>
                  <div className="text-white/50 text-[10px] truncate">{track.artist}</div>
                </div>
                <button onClick={() => setMusicOpen(true)} data-testid="director-music-change-btn" className="text-[10px] text-tv-accent font-semibold">Change</button>
              </div>
              <audio ref={audioElRef} src={track.preview_url} loop crossOrigin="anonymous" data-testid="director-music-audio" />
              <label className="block">
                <div className="flex items-center justify-between text-[10px] text-white/60 uppercase tracking-wider font-bold"><span className="flex items-center gap-1"><Volume2 className="w-3 h-3" /> Music</span><span>{Math.round(musicVol * 100)}%</span></div>
                <input type="range" data-testid="director-music-vol" min="0" max="1" step="0.05" value={musicVol} onChange={(e) => setMusicVol(parseFloat(e.target.value))} className="w-full accent-tv-accent" />
              </label>
              <label className="block">
                <div className="flex items-center justify-between text-[10px] text-white/60 uppercase tracking-wider font-bold"><span className="flex items-center gap-1"><MicVocal className="w-3 h-3" /> Mic</span><span>{Math.round(micVol * 100)}%</span></div>
                <input type="range" data-testid="director-mic-vol" min="0" max="1.5" step="0.05" value={micVol} onChange={(e) => setMicVol(parseFloat(e.target.value))} className="w-full accent-tv-accent" />
              </label>
              <button
                onClick={toggleMix}
                data-testid="director-mix-toggle"
                className={`w-full font-bold text-xs px-3 py-2 rounded-full flex items-center justify-center gap-1.5 ${mixOn ? "bg-tv-live text-white" : "bg-tv-accent hover:bg-yellow-400 text-black"}`}
              >
                {mixOn ? <><Pause className="w-3.5 h-3.5" /> Stop mix</> : <><Play className="w-3.5 h-3.5" /> Pipe mix to stream</>}
              </button>
              <div className="text-[10px] text-white/40">Mix sends mic + music as the stream's audio. Without mix, your mic goes out alone.</div>
            </div>
          )}
        </section>

        {/* 3. Newsroom overlays */}
        <section data-testid="director-newsroom" className="tv-glass rounded-xl border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-2 text-white font-display font-bold text-sm"><Newspaper className="w-4 h-4 text-tv-accent" /> Newsroom Overlays</div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3 text-tv-accent" /> One-tap presets</div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(NEWSROOM_PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`newsroom-preset-${key}`}
                  onClick={() => applyPreset(key)}
                  className="bg-white/5 hover:bg-tv-accent/20 hover:text-tv-accent border border-white/10 text-white/90 text-[11px] font-bold px-2 py-1.5 rounded transition"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-white/10 rounded-lg p-2 space-y-2">
            <label className="flex items-center gap-2 text-[11px] text-white"><input type="checkbox" data-testid="overlay-lower-third-toggle" checked={overlays.lower_third.visible} onChange={(e) => setOverlays({ ...overlays, lower_third: { ...overlays.lower_third, visible: e.target.checked } })} /> Lower-third</label>
            <input type="text" data-testid="overlay-lower-third-name" value={overlays.lower_third.name} onChange={(e) => setOverlays({ ...overlays, lower_third: { ...overlays.lower_third, name: e.target.value } })} placeholder="Name (e.g. Nova Reyes)" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent" />
            <input type="text" data-testid="overlay-lower-third-title" value={overlays.lower_third.title} onChange={(e) => setOverlays({ ...overlays, lower_third: { ...overlays.lower_third, title: e.target.value } })} placeholder="Title / role" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent" />
          </div>

          <div className="border border-white/10 rounded-lg p-2 space-y-2">
            <label className="flex items-center gap-2 text-[11px] text-white"><input type="checkbox" data-testid="overlay-breaking-toggle" checked={overlays.breaking.visible} onChange={(e) => setOverlays({ ...overlays, breaking: { ...overlays.breaking, visible: e.target.checked } })} /> Breaking banner</label>
            <input type="text" data-testid="overlay-breaking-text" value={overlays.breaking.text} onChange={(e) => setOverlays({ ...overlays, breaking: { ...overlays.breaking, text: e.target.value } })} placeholder="Breaking news text…" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent" />
          </div>

          <div className="border border-white/10 rounded-lg p-2 space-y-2">
            <label className="flex items-center gap-2 text-[11px] text-white"><input type="checkbox" data-testid="overlay-ticker-toggle" checked={overlays.ticker.visible} onChange={(e) => setOverlays({ ...overlays, ticker: { ...overlays.ticker, visible: e.target.checked } })} /> News ticker</label>
            <input type="text" data-testid="overlay-ticker-text" value={overlays.ticker.text} onChange={(e) => setOverlays({ ...overlays, ticker: { ...overlays.ticker, text: e.target.value } })} placeholder="Scrolling headlines…" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent" />
            <div className="flex items-center gap-2 text-[10px] text-white/60">
              <span>Speed</span>
              <input type="range" data-testid="overlay-ticker-speed" min="0" max="40" value={overlays.ticker.speed} onChange={(e) => setOverlays({ ...overlays, ticker: { ...overlays.ticker, speed: parseInt(e.target.value, 10) } })} className="flex-1 accent-tv-accent" />
            </div>
          </div>
        </section>
      </div>
      )}

      {!embedded && canAdvanced && (
      <div className="grid md:grid-cols-3 gap-4">
        {/* 4. Stage / Layouts + URL PIP */}
        <section data-testid="director-stage" className="tv-glass rounded-xl border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-2 text-white font-display font-bold text-sm"><Layers className="w-4 h-4 text-tv-accent" /> Stage layouts</div>
          <div className="grid grid-cols-4 gap-1.5">
            {Object.entries(LAYOUTS).map(([k, v]) => (
              <button
                key={k}
                data-testid={`stage-layout-${k}`}
                onClick={() => {
                  setLayout(k);
                  // Persist + broadcast so the right-rail buttons in
                  // DirectorConsole reflect the change.
                  try { localStorage.setItem("tapin_stage_layout", k); } catch {}
                  try { window.dispatchEvent(new CustomEvent("tapin:stage-layout", { detail: k })); } catch {}
                }}
                className={`text-[10px] font-bold px-2 py-1.5 rounded border transition ${layout === k ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white/80 border-white/10 hover:bg-white/10"}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {/* Pointer back to the guest manager — hosts kept missing the
              fact that CAM 1 / 2 / 3 assignments drive which guest shows
              up in each stage slot. Anchor scrolls to the GuestManager
              section mounted further down the Director page. */}
          <a
            href="#director-guests"
            data-testid="stage-guest-hint"
            className="block text-[10px] text-white/55 hover:text-white transition border border-dashed border-white/10 hover:border-white/25 rounded px-2 py-1.5 leading-snug"
          >
            Multi-cam + PIP layouts show guests assigned to CAM 1 / 2 / 3 — manage slots in the Guests panel ↓
          </a>
          <div className="border-t border-white/10 pt-3 space-y-2">
            <div className="flex items-center gap-2 text-white text-xs font-semibold"><Link2 className="w-3.5 h-3.5 text-tv-accent" /> URL PIP (YouTube / TikTok / social)</div>
            <input
              value={urlPip.url}
              onChange={(e) => setUrlPip({ ...urlPip, url: e.target.value })}
              placeholder="Paste a YouTube / Vimeo / TikTok / Instagram URL"
              data-testid="url-pip-input"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent"
            />
            <input
              value={urlPip.label}
              onChange={(e) => setUrlPip({ ...urlPip, label: e.target.value })}
              placeholder="PIP label"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent"
            />
            <label className="flex items-center gap-2 text-[11px] text-white">
              <input type="checkbox" data-testid="url-pip-toggle" checked={urlPip.visible} onChange={(e) => setUrlPip({ ...urlPip, visible: e.target.checked && !!toEmbedUrl(urlPip.url) })} />
              Show URL PIP
              {urlPip.url && !toEmbedUrl(urlPip.url) && <span className="text-red-400 text-[10px] ml-auto">Unsupported URL</span>}
            </label>
          </div>
        </section>

        {/* 5. Weather + BRB */}
        <section data-testid="director-weather-brb" className="tv-glass rounded-xl border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-2 text-white font-display font-bold text-sm"><Cloud className="w-4 h-4 text-tv-accent" /> Weather & BRB</div>
          <div className="border border-white/10 rounded p-2 space-y-2">
            <label className="flex items-center gap-2 text-[11px] text-white"><input type="checkbox" data-testid="weather-toggle" checked={weather.visible} onChange={(e) => setWeather({ ...weather, visible: e.target.checked })} /> Show weather</label>
            <input
              value={weather.location}
              onChange={(e) => setWeather({ ...weather, location: e.target.value })}
              placeholder="City (blank = auto)"
              data-testid="weather-location"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent"
            />
          </div>
          <div className="border border-white/10 rounded p-2 space-y-2">
            <label className="flex items-center gap-2 text-[11px] text-white"><Coffee className="w-3.5 h-3.5 text-tv-accent" /><input type="checkbox" data-testid="brb-toggle" checked={brb.visible} onChange={(e) => setBrb({ ...brb, visible: e.target.checked })} /> Be Right Back</label>
            <input
              value={brb.text}
              onChange={(e) => setBrb({ ...brb, text: e.target.value })}
              placeholder="Be Right Back"
              data-testid="brb-text"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent"
            />
            <div className="flex items-center gap-2">
              <input type="file" accept="image/*" ref={brbFileRef} className="hidden" onChange={async (e) => {
                const f = e.target.files?.[0]; e.target.value = "";
                if (!f) return;
                const fd = new FormData(); fd.append("file", f);
                try {
                  const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  setBrb((b) => ({ ...b, image_url: `/api/files/${data.storage_path}` }));
                  toast.success("BRB background uploaded");
                } catch (err) { toast.error(formatApiError(err)); }
              }} />
              <button type="button" onClick={() => brbFileRef.current?.click()} data-testid="brb-upload-btn" className="flex-1 bg-white/10 hover:bg-white/15 text-white text-[11px] font-bold px-2 py-1.5 rounded flex items-center justify-center gap-1"><Upload className="w-3 h-3" /> {brb.image_url ? "Replace image" : "Upload image"}</button>
              {brb.image_url && <button type="button" onClick={() => setBrb({ ...brb, image_url: "" })} className="text-white/50 hover:text-white text-[10px]">Clear</button>}
            </div>
          </div>
        </section>

        {/* 6. Announcer FX + Music library */}
        <section data-testid="director-audio-pro" className="tv-glass rounded-xl border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-2 text-white font-display font-bold text-sm"><Wand2 className="w-4 h-4 text-tv-accent" /> Announcer FX</div>
          <select
            value={announcer}
            onChange={(e) => { setAnnouncer(e.target.value); if (audioCtxRef.current) buildAnnouncerChain(audioCtxRef.current, e.target.value); }}
            data-testid="announcer-preset-select"
            className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-tv-accent"
          >
            {Object.entries(ANNOUNCER_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="text-[10px] text-white/40">Applied to mic channel. Pipe the mix to go out on stream.</div>

          <div className="border-t border-white/10 pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-white text-xs font-semibold"><MicIcon className="w-3.5 h-3.5 text-tv-accent" /> My music library</div>
              <button type="button" onClick={async () => {
                try { const { data } = await api.get("/music/library"); setMusicLibrary(data || []); }
                catch (err) { toast.error(formatApiError(err)); }
              }} data-testid="music-library-refresh" className="text-[10px] text-tv-brand hover:text-tv-accent">Refresh</button>
            </div>
            <input type="file" accept="audio/*" ref={musicUploadRef} className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]; e.target.value = "";
              if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              const qs = `?title=${encodeURIComponent(f.name.replace(/\.[^.]+$/, ""))}`;
              try {
                const { data } = await api.post(`/music/library/upload${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                setMusicLibrary((l) => [data, ...l]);
                toast.success("Track uploaded");
              } catch (err) { toast.error(formatApiError(err)); }
            }} />
            <button type="button" onClick={() => musicUploadRef.current?.click()} data-testid="music-upload-btn" className="w-full bg-tv-accent hover:bg-yellow-400 text-black font-bold text-[11px] px-2 py-1.5 rounded flex items-center justify-center gap-1"><Upload className="w-3 h-3" /> Upload audio</button>
            <div className="max-h-40 overflow-y-auto space-y-1" data-testid="music-library-list">
              {musicLibrary.length === 0 && <div className="text-white/40 text-[10px]">Upload MP3/WAV tracks, queue them as a playlist.</div>}
              {musicLibrary.map((t) => (
                <div key={t.id} className="flex items-center gap-2 bg-white/5 rounded px-2 py-1">
                  <div className="flex-1 min-w-0"><div className="text-white text-[11px] font-semibold truncate">{t.title}</div><div className="text-white/40 text-[10px] truncate">{t.artist}</div></div>
                  <button type="button" onClick={() => setPlaylist((p) => [...p, t])} data-testid={`music-queue-${t.id}`} className="text-[10px] text-tv-accent hover:text-yellow-300 font-bold">+ Queue</button>
                </div>
              ))}
            </div>
            {playlist.length > 0 && (
              <div className="border-t border-white/10 pt-2">
                <div className="text-white text-xs font-semibold mb-1">Playlist ({playlist.length})</div>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {playlist.map((t, i) => (
                    <div key={`${t.id}-${i}`} className="flex items-center gap-2 bg-white/5 rounded px-2 py-1 text-[11px]">
                      <span className={`${i === playlistIdx ? "text-tv-accent font-bold" : "text-white/70"} flex-1 truncate`}>{i + 1}. {t.title}</span>
                      <button onClick={() => setPlaylist((p) => p.filter((_, j) => j !== i))} className="text-red-400 text-[10px]">×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      )}

      {!embedded && canAdvanced && (
        <section data-testid="director-soundboard-section" className="tv-glass rounded-xl border border-white/10 p-4">
          <DirectorSoundboard
            ctx={audioCtxRef.current}
            dest={destRef.current}
            disabled={!mixOn}
          />
        </section>
      )}

      <MusicPicker open={musicOpen} onClose={() => setMusicOpen(false)} onSelect={(t) => setTrack(t)} />
    </div>
  );
}
